import React from "react";
import Head from "next/head";
import Layout from "../components/layout";
import { GetStaticProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../next-i18next.config";
import "../styles/scss/observability-survey.module.scss";
import Image from "next/image";
import { SurveyQuestionsIcon } from "../components/v2/icons-header";
import ObservabilitySurveyForm from "../components/v2/form/observability-survey";
import { useTranslation, Trans } from "next-i18next";

const SURVEY_NS = "observability-survey-2026" as const;

const FINDING_BLOCKS = [
    { id: "runningMultiple", image: "/images/running-multiple-tools.svg" },
    { id: "setupFriction", image: "/images/set-up-friction-slows-down-adoption.svg" },
    { id: "automation", image: "/images/automation-now-requirement.svg" },
    { id: "integration", image: "/images/better-integration-signal.svg" },
] as const;

const ObservabilitySurvey2026: React.FC = () => {
    const { t } = useTranslation(SURVEY_NS);

    const aboutBullets = t("about.bullets", { returnObjects: true }) as string[];
    const audienceTags = t("about.audience", { returnObjects: true }) as string[];
    const bullets = Array.isArray(aboutBullets) ? aboutBullets : [];
    const audience = Array.isArray(audienceTags) ? audienceTags : [];

    return (
        <>
            <Head>
                <link
                    rel="preload"
                    as="image"
                    href="/images/observability-survey-2026-mobile.webp"
                    media="(max-width: 768px)"
                    fetchPriority="high"
                />
                <link
                    rel="preload"
                    as="image"
                    href="/images/observability-survey-2026.webp"
                    media="(min-width: 769px)"
                    fetchPriority="high"
                />
            </Head>
            <Layout
                isHomePage={false}
                pageTitle={"State of Observability 2026 Report for DevOps Leaders & CTOs"}
                ogImage={t("meta.ogImage")}
                uri="/observability-survey-2026/"
                metaTitle={"State of Observability 2026 Report for DevOps Leaders & CTOs"}
                metaDesc={"Explore the State of Observability 2026 report with insights from 400+ DevOps leaders, SREs, and CTOs. Learn trends in tools, AI, and platform decisions."}
                metaRobotsNoindex="index"
                metaRobotsNofollow="follow">
                <div className="mw-survey-page">
                    <div className="survey-hero-section">
                        <div className="survey-hero-content">
                            <div className="container">
                                <h1>
                                    {t("hero.titleBeforeBreak")} <br /> {t("hero.titleAfterBreak")}
                                </h1>
                                <p>
                                    {t("hero.subtitleBeforeBreak")} <br /> {t("hero.subtitleAfterBreak")}
                                </p>
                                <div className="d-flex">
                                    <a className="sign-up ahref-btn primary" href="#about-the-report">
                                        {t("hero.ctaDownload")}
                                    </a>
                                    <a className="sign-up ahref-btn secondary" href="#key-finding">
                                        {t("hero.ctaInside")}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="survey-overview">
                        <div className="container">
                            <div className="d-flex">
                                <div className="description">
                                    <h4>{t("overview.respondentsValue")}</h4>
                                    <p>{t("overview.respondentsLabel")}</p>
                                </div>
                                <div className="description">
                                    <h4>{t("overview.industriesValue")}</h4>
                                    <p>{t("overview.industriesLabel")}</p>
                                </div>
                                <div className="description">
                                    <h4>{t("overview.periodValue")}</h4>
                                    <p>{t("overview.periodLabel")}</p>
                                </div>
                                <div className="description">
                                    <h4>{t("overview.chaptersValue")}</h4>
                                    <p>{t("overview.chaptersLabel")}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="survey-features" id="key-finding">
                        <div className="container">
                            <div className={"section-tag"}>{t("features.sectionTag")}</div>
                            <h2>
                                <Trans
                                    i18nKey="features.sectionTitle"
                                    ns={SURVEY_NS}
                                    components={{ highlight: <span /> }}
                                />
                            </h2>
                            <p>
                                {t("features.introBeforeBreak")} <br /> {t("features.introAfterBreak")}
                            </p>
                            <div className="features">
                                {FINDING_BLOCKS.map(({ id, image }) => {
                                    const pointsRaw = t(`findings.${id}.points`, { returnObjects: true });
                                    const points = Array.isArray(pointsRaw) ? (pointsRaw as string[]) : [];
                                    return (
                                        <div className="d-flex" key={id}>
                                            <div className="left-block">
                                                <h3>{t(`findings.${id}.title`)}</h3>
                                                <ul>
                                                    {points.map((item, i) => (
                                                        <li key={i}>{item}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div className="right-block">
                                                <Image
                                                    src={image}
                                                    alt={t(`findings.${id}.imageAlt`)}
                                                    width={100}
                                                    height={100}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="survey-features-bg-wave">
                            <Image
                                src="/images/survey-features-bg-wave.svg"
                                alt="Survey Features Background Wave"
                                width={100}
                                height={100}
                            />
                        </div>
                    </div>

                    <div className="survey-small-cta">
                        <div className="container">
                            <div className="small-cta">
                                <h2>Explore More Findings.</h2>
                                <a className="sign-up ahref-btn glossy-btn" href="#about-the-report">
                                    Download Report Now
                                </a>
                            </div>

                        </div>
                    </div>

                    <div className="survey-testimonial">
                        <div className="container">
                            <SurveyQuestionsIcon />
                            <p>
                                {t("testimonial.quoteBeforeBreak")} <br /> {t("testimonial.quoteAfterBreak")}
                            </p>
                            <p className="about-description">{t("testimonial.attribution")}</p>
                        </div>
                    </div>

                    <div className="about-the-report" id="about-the-report">
                        <div className="container">
                            <div className="d-flex">
                                <div className="left-block">
                                    <div className="section-tag">{t("about.sectionTag")}</div>
                                    <h2>{t("about.title")}</h2>
                                    <ul>
                                        {bullets.map((item, i) => (
                                            <li key={i}>{item}</li>
                                        ))}
                                    </ul>
                                    <h4>{t("about.whoTitle")}</h4>
                                    <div className="who-read-tag">
                                        {audience.map((label, i) => (
                                            <p key={i}>{label}</p>
                                        ))}
                                    </div>
                                </div>
                                <div className="right-block">
                                    <ObservabilitySurveyForm />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        </>
    );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
    return {
        props: {
            ...(await serverSideTranslations(
                locale ?? "en",
                ["common", "header", "footer", SURVEY_NS],
                nextI18NextConfig
            )),
            pageData: "",
            pageTitle: "State of Observability 2026 Report for DevOps Leaders & CTOs",
            description:
                "Explore the State of Observability 2026 report with insights from 400+ DevOps leaders, SREs, and CTOs. Learn trends in tools, AI, and platform decisions.",
            uri: "/observability-survey-2026/",
            ogImage: "/images/observability-survey-2026-thumbnail.webp",
            metaTitle: "State of Observability 2026 Report for DevOps Leaders & CTOs",
            metaDesc:
                "Explore the State of Observability 2026 report with insights from 400+ DevOps leaders, SREs, and CTOs. Learn trends in tools, AI, and platform decisions.",
            metaRobotsNoindex: "index",
            metaRobotsNofollow: "follow",
        },
    };
};

export default ObservabilitySurvey2026;
